import * as THREE from "three";
import type { FrameContext, QualityLevel, RuntimeSystem } from "../core/types";
import { createEnvironmentTarget, smoothEnvironmentState, type EnvironmentState, type Rgb } from "./transitions";
import { createWaterMaterial, type WaterMaterialControls } from "./water";

const MAX_RAIN = 4200;
const MAX_FIREFLIES = 180;

const colorFrom = (rgb: Rgb): THREE.Color => new THREE.Color().setRGB(...rgb);

function makeSkyMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: "PhysicalGradientSky",
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uTop: { value: new THREE.Color() },
      uHorizon: { value: new THREE.Color() },
      uSunColor: { value: new THREE.Color() },
      uSunDirection: { value: new THREE.Vector3(0, 1, 0) },
      uCloudCover: { value: 0 },
      uTime: { value: 0 },
    },
    vertexShader: `
      varying vec3 vWorldDirection;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldDirection = normalize(worldPosition.xyz - cameraPosition);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_Position.z = gl_Position.w;
      }
    `,
    fragmentShader: `
      uniform vec3 uTop;
      uniform vec3 uHorizon;
      uniform vec3 uSunColor;
      uniform vec3 uSunDirection;
      uniform float uCloudCover;
      uniform float uTime;
      varying vec3 vWorldDirection;

      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        float a = fract(sin(dot(i, vec2(127.1, 311.7))) * 43758.5453);
        float b = fract(sin(dot(i + vec2(1.0, 0.0), vec2(127.1, 311.7))) * 43758.5453);
        float c = fract(sin(dot(i + vec2(0.0, 1.0), vec2(127.1, 311.7))) * 43758.5453);
        float d = fract(sin(dot(i + vec2(1.0), vec2(127.1, 311.7))) * 43758.5453);
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }

      void main() {
        vec3 direction = normalize(vWorldDirection);
        float height = smoothstep(-0.08, 0.82, direction.y);
        vec3 color = mix(uHorizon, uTop, pow(height, 0.68));
        float sunAngle = max(dot(direction, normalize(uSunDirection)), 0.0);
         float cloudSun = 1.0 - uCloudCover * 0.82;
         float mieGlow = pow(sunAngle, 24.0) * 0.22 * cloudSun;
         float sunDisk = pow(sunAngle, 1100.0) * cloudSun;
         color += uSunColor * (mieGlow + sunDisk * 0.72);
        vec2 cloudUv = direction.xz / max(0.15, direction.y + 0.32) * 0.72;
        float clouds = noise(cloudUv * 2.1 + vec2(uTime * 0.003, 0.0));
        clouds += noise(cloudUv * 4.2 - vec2(uTime * 0.004, 0.0)) * 0.5;
        clouds = smoothstep(1.05 - uCloudCover * 0.62, 1.42, clouds);
        color = mix(color, mix(uHorizon, vec3(0.9), 0.42), clouds * uCloudCover * 0.5 * smoothstep(0.0, 0.2, direction.y));
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
}

function makeMistMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: "LayeredGroundMist",
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      uTime: { value: 0 },
      uOpacity: { value: 0 },
      uColor: { value: new THREE.Color() },
      uOffset: { value: new THREE.Vector2() },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform float uOpacity;
      uniform vec3 uColor;
      uniform vec2 uOffset;
      varying vec2 vUv;

      float noise(vec2 p) {
        vec2 i = floor(p); vec2 f = fract(p); f = f*f*(3.0-2.0*f);
        float a = fract(sin(dot(i, vec2(12.9898, 78.233))) * 43758.5453);
        float b = fract(sin(dot(i + vec2(1,0), vec2(12.9898, 78.233))) * 43758.5453);
        float c = fract(sin(dot(i + vec2(0,1), vec2(12.9898, 78.233))) * 43758.5453);
        float d = fract(sin(dot(i + vec2(1,1), vec2(12.9898, 78.233))) * 43758.5453);
        return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
      }

      void main() {
        vec2 centered = vUv - 0.5;
        float edge = smoothstep(0.5, 0.18, length(centered));
        vec2 p = vUv * 4.0 + uOffset + vec2(uTime * 0.012, uTime * -0.006);
        float vapor = noise(p) * 0.65 + noise(p * 2.2) * 0.35;
        float alpha = edge * smoothstep(0.22, 0.86, vapor) * uOpacity;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });
}

function makeRain(): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
  const positions = new Float32Array(MAX_RAIN * 3);
  let seed = 0x71a9b3;
  const random = (): number => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  for (let i = 0; i < MAX_RAIN; i += 1) {
    const offset = i * 3;
    positions[offset] = (random() - 0.5) * 76;
    positions[offset + 1] = random() * 36 - 4;
    positions[offset + 2] = (random() - 0.5) * 76;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color: 0xc8d8d6,
    size: 0.075,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  points.renderOrder = 8;
  return points;
}

function makeFireflies(): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
  const positions = new Float32Array(MAX_FIREFLIES * 3);
  for (let i = 0; i < MAX_FIREFLIES; i += 1) {
    const angle = i * 2.399963;
    const radius = 4 + ((i * 29) % 100) * 0.25;
    positions[i * 3] = Math.cos(angle) * radius;
    positions[i * 3 + 1] = 0.35 + ((i * 17) % 30) * 0.075;
    positions[i * 3 + 2] = Math.sin(angle) * radius;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color: 0xc7dc79,
    size: 0.12,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  points.renderOrder = 7;
  return points;
}

export class EnvironmentSystem implements RuntimeSystem {
  readonly water: WaterMaterialControls;
  private readonly scene: THREE.Scene;
  private readonly root = new THREE.Group();
  private readonly sky: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  private readonly sun = new THREE.DirectionalLight();
  private readonly sunTarget = new THREE.Object3D();
  private readonly hemisphere = new THREE.HemisphereLight();
  private readonly ambientFill = new THREE.AmbientLight(0xd9e0dc);
  private readonly rain = makeRain();
  private readonly fireflies = makeFireflies();
  private readonly mistLayers: Array<THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>> = [];
  private readonly previousFog: THREE.Fog | THREE.FogExp2 | null;
  private readonly previousBackground: THREE.Color | THREE.Texture | THREE.CubeTexture | null;
  private state: EnvironmentState | undefined;
  private quality: QualityLevel | undefined;
  private rendererConfigured = false;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.previousFog = scene.fog;
    this.previousBackground = scene.background;
    this.root.name = "Environment";

    this.sky = new THREE.Mesh(new THREE.SphereGeometry(620, 32, 16), makeSkyMaterial());
    this.sky.name = "SkyDome";
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -100;
    this.root.add(this.sky);

    this.sun.name = "Sun";
    this.sun.castShadow = true;
    this.sun.shadow.bias = -0.00025;
    this.sun.shadow.normalBias = 0.025;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 300;
    this.sun.target = this.sunTarget;
    this.ambientFill.name = "CoolAmbientFill";
    this.root.add(this.sun, this.sunTarget, this.hemisphere, this.ambientFill);

    const mistGeometry = new THREE.PlaneGeometry(150, 150, 1, 1);
    for (let i = 0; i < 4; i += 1) {
      const material = makeMistMaterial();
      material.uniforms.uOffset!.value.set(i * 3.7, i * -2.1);
      const layer = new THREE.Mesh(mistGeometry, material);
      layer.name = `MistLayer${i + 1}`;
      layer.rotation.x = -Math.PI / 2;
      layer.position.set((i - 1.5) * 22, 0.24 + i * 0.24, ((i * 31) % 45) - 20);
      layer.renderOrder = 5 + i;
      this.mistLayers.push(layer);
      this.root.add(layer);
    }

    this.root.add(this.rain, this.fireflies);
    this.scene.add(this.root);
    this.water = createWaterMaterial();
  }

  get waterMaterial(): THREE.ShaderMaterial {
    return this.water.material;
  }

  update(context: FrameContext): void {
    const target = createEnvironmentTarget(
      context.settings.weather,
      context.settings.timePreset,
      context.elapsed,
      context.settings.timeFlow,
    );
    this.state = this.state
      ? smoothEnvironmentState(this.state, target, Math.min(context.delta, 0.1))
      : target;

    if (!this.rendererConfigured) {
      context.renderer.shadowMap.enabled = true;
      context.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      context.renderer.outputColorSpace = THREE.SRGBColorSpace;
      context.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      context.renderer.toneMappingExposure = 1.02;
      this.rendererConfigured = true;
    }
    if (this.quality !== context.settings.quality) this.applyQuality(context.settings.quality);

    const state = this.state;
    this.root.position.x = context.camera.position.x;
    this.root.position.z = context.camera.position.z;
    this.sky.position.y = context.camera.position.y;

    const sunDirection = new THREE.Vector3(
      Math.cos(state.sunAzimuth) * Math.cos(state.sunElevation),
      Math.sin(state.sunElevation),
      Math.sin(state.sunAzimuth) * Math.cos(state.sunElevation),
    ).normalize();
    this.sun.position.copy(sunDirection).multiplyScalar(125);
    this.sun.position.y += context.camera.position.y;
    this.sunTarget.position.set(0, context.camera.position.y - 1, 0);
    this.sun.intensity = state.sunIntensity * (1 - Math.sin(context.elapsed * 0.035) * state.cloudCover * 0.07);
    this.sun.color.setRGB(...state.sunColor);
    this.hemisphere.intensity = state.hemisphereIntensity;
    this.hemisphere.color.copy(colorFrom(state.skyHorizon));
    this.hemisphere.groundColor.setRGB(0.2, 0.24, 0.21).lerp(colorFrom(state.fogColor), 0.18 + state.wetness * 0.16);
    this.ambientFill.intensity = 0.08 + state.hemisphereIntensity * 0.11 + state.mist * 0.05;

    const skyUniforms = this.sky.material.uniforms;
    skyUniforms.uTop!.value.setRGB(...state.skyTop);
    skyUniforms.uHorizon!.value.setRGB(...state.skyHorizon);
    skyUniforms.uSunColor!.value.setRGB(...state.sunColor);
    skyUniforms.uSunDirection!.value.copy(sunDirection);
    skyUniforms.uCloudCover!.value = state.cloudCover;
    skyUniforms.uTime!.value = context.elapsed;

    const fog = this.scene.fog instanceof THREE.Fog ? this.scene.fog : new THREE.Fog(0xffffff);
    fog.color.setRGB(...state.fogColor);
    fog.near = state.fogNear;
    fog.far = Math.max(state.fogNear + 12, state.fogFar);
    this.scene.fog = fog;
    this.scene.background = null;

    this.updateMist(context.elapsed, state);
    this.updateRain(context.delta, context.elapsed, state);
    this.updateFireflies(context.elapsed, state);
    this.water.update(context.elapsed, state.wetness, state.rain, state.skyHorizon, state.waterColor, state.fogColor, fog.near, fog.far);
  }

  private applyQuality(quality: QualityLevel): void {
    this.quality = quality;
    const high = quality === "high";
    const shadowSize = high ? 2048 : 1024;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    this.sun.shadow.camera.left = high ? -70 : -46;
    this.sun.shadow.camera.right = high ? 70 : 46;
    this.sun.shadow.camera.top = high ? 70 : 46;
    this.sun.shadow.camera.bottom = high ? -70 : -46;
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
    this.rain.geometry.setDrawRange(0, high ? MAX_RAIN : 1700);
    this.fireflies.geometry.setDrawRange(0, high ? MAX_FIREFLIES : 80);
    this.mistLayers.forEach((layer, index) => { layer.visible = high || index < 2; });
  }

  private updateMist(elapsed: number, state: EnvironmentState): void {
    for (let i = 0; i < this.mistLayers.length; i += 1) {
      const layer = this.mistLayers[i];
      if (!layer) continue;
      layer.rotation.z = elapsed * (0.0015 + i * 0.0004) * (0.5 + state.wind);
      layer.material.uniforms.uTime!.value = elapsed;
      layer.material.uniforms.uOpacity!.value = state.mist * (0.14 - i * 0.014);
      layer.material.uniforms.uColor!.value.setRGB(...state.fogColor);
    }
  }

  private updateRain(delta: number, elapsed: number, state: EnvironmentState): void {
    this.rain.material.opacity = state.rain * 0.66;
    this.rain.visible = state.rain > 0.012;
    if (!this.rain.visible) return;
    const attribute = this.rain.geometry.getAttribute("position") as THREE.BufferAttribute;
    const positions = attribute.array as Float32Array;
    const fall = delta * (20 + state.rain * 18);
    const drift = delta * state.wind * 1.8;
    for (let i = 0; i < this.rain.geometry.drawRange.count; i += 1) {
      const offset = i * 3;
      const y = positions[offset + 1];
      const x = positions[offset];
      if (y === undefined || x === undefined) continue;
      positions[offset + 1] = y - fall;
      positions[offset] = x + drift;
      if (positions[offset + 1]! < -3) {
        positions[offset + 1] = 30 + ((i * 19 + elapsed * 3) % 8);
        positions[offset] = ((i * 47) % 760) / 10 - 38;
      }
    }
    attribute.needsUpdate = this.rain.visible;
  }

  private updateFireflies(elapsed: number, state: EnvironmentState): void {
    this.fireflies.material.opacity = state.fireflies * (0.48 + Math.sin(elapsed * 1.4) * 0.16);
    this.fireflies.visible = state.fireflies > 0.01;
    this.fireflies.rotation.y = elapsed * 0.012;
    this.fireflies.position.y = Math.sin(elapsed * 0.27) * 0.12;
  }

  dispose(): void {
    this.scene.remove(this.root);
    this.scene.fog = this.previousFog;
    this.scene.background = this.previousBackground;
    this.sky.geometry.dispose();
    this.sky.material.dispose();
    this.rain.geometry.dispose();
    this.rain.material.dispose();
    this.fireflies.geometry.dispose();
    this.fireflies.material.dispose();
    const mistGeometry = this.mistLayers[0]?.geometry;
    for (const layer of this.mistLayers) layer.material.dispose();
    mistGeometry?.dispose();
    this.water.material.dispose();
    this.sun.shadow.map?.dispose();
  }
}
